/*!
 * hh-events.js :: vendor-agnostic event bus + dataLayer scaffold
 * Cycle 1 of advance-everywhere-v6 :: dimension analytics_data
 *
 * ============================================================================
 * SCHEMA
 * ============================================================================
 * Event names: snake_case. Namespace prefixes:
 *   product_*    - PDP-level interactions (product_card_click, product_view)
 *   cart_*       - cart lifecycle (cart_add, cart_open, cart_close, cart_updated)
 *   search_*     - search dialog + predictive submit (search_submit)
 *   newsletter_* - newsletter form (newsletter_submit)
 *   nav_*        - navigation clicks (nav_top_click)
 *
 * Currently instrumented (cycle 1):
 *   - cart_add            : ATC button click on PDP
 *   - cart_open           : cart drawer trigger
 *   - cart_close          : cart drawer close
 *   - cart_updated        : after /cart/add.js or /cart/change.js (fired from global.js)
 *   - search_submit       : predictive search form submit
 *   - newsletter_submit   : newsletter form submit
 *   - product_card_click  : product card link click
 *   - nav_top_click       : top-level main nav link click
 *
 * Public API:
 *   window.HH.events.track(name, props)
 *   window.HH.events.on(name, fn)
 *   window.HH.events.off(name, fn)
 *
 * Auto-emit attributes (vendor-neutral; no JS required at the call site):
 *   <el data-hh-event="cart_add" data-hh-event-props='{"variant_id":1}'>
 *   <form data-hh-event="newsletter_submit" data-hh-event-on="submit">
 *
 * Sinks (best-effort, all wrapped in try/catch):
 *   - window.dataLayer (GTM)
 *   - window.klaviyo (push ['track', name, props])
 *   - window.fbq ('trackCustom', name, props)
 *   - document CustomEvent 'hh:event' (for other in-theme listeners)
 *
 * Debug: append ?hhdebug=1 to any URL to console.debug every event.
 *
 * Queue-before-ready: any HH.events.track() call that happens before this
 * script parses lands in window.HH._eventQueue and is flushed on init.
 *
 * Size: < 3KB source, no deps, defer-loaded.
 * ============================================================================
 */
(function () {
  'use strict';

  var w = window;
  var d = document;

  // dataLayer must exist before any GTM container or this script runs.
  w.dataLayer = w.dataLayer || [];

  // Establish HH namespace + capture any pre-init queue.
  w.HH = w.HH || {};
  var preQueue = (w.HH._eventQueue && w.HH._eventQueue.slice) ? w.HH._eventQueue.slice() : [];
  w.HH._eventQueue = []; // keep the array but treat it as drained.

  // Internal subscriber map.
  var subs = new Map();

  var debugOn = (function () {
    try { return new URLSearchParams(w.location.search).get('hhdebug') === '1'; }
    catch (_e) { return false; }
  })();

  function track(name, props) {
    if (!name) return;
    props = props || {};
    var payload = Object.assign({ event: name }, props, { ts: Date.now() });

    // 1. dataLayer (GTM, GA4 via tag manager)
    try { w.dataLayer.push(payload); } catch (_e) {}

    // 2. Klaviyo
    try { if (w.klaviyo && typeof w.klaviyo.push === 'function') w.klaviyo.push(['track', name, props]); } catch (_e) {}

    // 3. Meta pixel
    try { if (typeof w.fbq === 'function') w.fbq('trackCustom', name, props); } catch (_e) {}

    // 4. Internal CustomEvent fan-out (other theme code can listen on document)
    try { d.dispatchEvent(new CustomEvent('hh:event', { detail: { name: name, props: props } })); } catch (_e) {}

    // 5. Direct subscribers via on()
    var set = subs.get(name);
    if (set) {
      set.forEach(function (fn) {
        try { fn(props, name); } catch (_e) {}
      });
    }

    if (debugOn) {
      try { console.debug('[hh:event]', name, props); } catch (_e) {}
    }
  }

  function on(name, fn) {
    if (!name || typeof fn !== 'function') return;
    var set = subs.get(name);
    if (!set) { set = new Set(); subs.set(name, set); }
    set.add(fn);
  }

  function off(name, fn) {
    var set = subs.get(name);
    if (!set) return;
    if (fn) set.delete(fn);
    else set.clear();
  }

  // Expose API. Preserve any existing HH.events fields, override track/on/off.
  w.HH.events = Object.assign(w.HH.events || {}, {
    track: track,
    on: on,
    off: off
  });

  // Drain pre-init queue. Items can be either ['name', props] or { name, props }.
  preQueue.forEach(function (entry) {
    try {
      if (Array.isArray(entry)) track(entry[0], entry[1]);
      else if (entry && entry.name) track(entry.name, entry.props);
    } catch (_e) {}
  });

  // ---------- Auto-emit: delegated click ----------
  function readProps(el) {
    var raw = el.getAttribute('data-hh-event-props');
    if (!raw) return {};
    try { return JSON.parse(raw); }
    catch (_e) { return {}; }
  }

  d.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var el = target.closest('[data-hh-event]');
    if (!el) return;
    // Skip submit-mode elements; they fire on form submit, not click.
    if (el.getAttribute('data-hh-event-on') === 'submit') return;
    var name = el.getAttribute('data-hh-event');
    if (!name) return;
    track(name, readProps(el));
  }, { passive: true, capture: false });

  // ---------- Auto-emit: delegated submit (capture phase to catch all forms) ----------
  d.addEventListener('submit', function (e) {
    var target = e.target;
    if (!target || typeof target.matches !== 'function') return;
    // The submit listener accepts either the form itself carrying the attrs,
    // or any ancestor with the attrs; do not prevent default.
    var el = target.matches('[data-hh-event][data-hh-event-on="submit"]')
      ? target
      : (typeof target.closest === 'function' ? target.closest('[data-hh-event][data-hh-event-on="submit"]') : null);
    if (!el) return;
    var name = el.getAttribute('data-hh-event');
    if (!name) return;
    try { track(name, readProps(el)); } catch (_e) {}
  }, { capture: true });

  // ============================================================================
  // Cycle 3 :: dimension accessibility :: HH.a11y
  // ============================================================================
  // Global polite + assertive live region helper. Reads from
  // <div id="hh-live-polite"> and <div id="hh-live-assertive"> rendered by
  // snippets/hh-a11y-live.liquid in layout/theme.liquid. Subscribes to the
  // cart_* lifecycle on the cycle 1 bus and reads short status sentences for
  // assistive technology. Idempotent: bails out if HH.a11y already exists.
  // Strings come from window.theme.strings.a11y when set, else hardcoded
  // English fallbacks.
  // ============================================================================
  if (!w.HH.a11y) {
    var strings = (w.theme && w.theme.strings && w.theme.strings.a11y) || {};
    var currency = (w.theme && w.theme.currency) || 'GBP';

    var moneyFormatter = null;
    try {
      if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
        moneyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency });
      }
    } catch (_e) {}

    function formatMoney(cents) {
      var amount = (typeof cents === 'number' ? cents : parseFloat(cents)) / 100;
      if (!isFinite(amount)) return '';
      // Prefer the shared market-correct formatter (matches the cart UI exactly).
      if (w.hhFormatMoney) {
        try { return w.hhFormatMoney(Math.round(amount * 100)); } catch (_e) {}
      }
      if (moneyFormatter) {
        try { return moneyFormatter.format(amount); } catch (_e) {}
      }
      return amount.toFixed(2);
    }

    function fillTemplate(tpl, vars) {
      if (!tpl) return '';
      return String(tpl).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, key) {
        return vars[key] != null ? String(vars[key]) : '';
      });
    }

    function announce(msg, opts) {
      if (!msg) return;
      var assertive = !!(opts && opts.assertive);
      var id = assertive ? 'hh-live-assertive' : 'hh-live-polite';
      var el = d.getElementById(id);
      if (!el) return;
      // Reset to retrigger when same string repeats.
      el.textContent = '';
      // Force reflow so screen readers see a true change.
      void el.offsetHeight;
      el.textContent = String(msg);
      var timerKey = assertive ? '_assertiveTimer' : '_politeTimer';
      try { clearTimeout(w.HH.a11y[timerKey]); } catch (_e) {}
      w.HH.a11y[timerKey] = setTimeout(function () {
        if (el.textContent === String(msg)) el.textContent = '';
      }, 2000);
    }

    w.HH.a11y = {
      announce: announce,
      _politeTimer: null,
      _assertiveTimer: null
    };

    // ---------- Bus subscriptions ----------
    // cart_add fires synchronously on the click, before fetch resolves; gives
    // immediate feedback. cart_updated fires after the fetch with truth from
    // /cart.js so we can announce a count + total. Both paths are debounced
    // independently so a quick "add then add again" coalesces.
    var _updatedTimer = null;

    function announceAdd(props) {
      var title = (props && props.title) || '';
      var tpl = strings.bag_item_added || '{{ title }} added to your bag';
      var fallback = 'Item added to your bag';
      announce(title ? fillTemplate(tpl, { title: title }) : fallback);
    }

    function announceUpdated(props) {
      // Debounce 250ms; quick mutations from QV/quick-add can fire twice.
      try { clearTimeout(_updatedTimer); } catch (_e) {}
      _updatedTimer = setTimeout(function () {
        var fetchOpts = { credentials: 'same-origin', headers: { 'Accept': 'application/json' } };
        var go = function (cart) {
          var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
          var total = cart && typeof cart.total_price === 'number' ? formatMoney(cart.total_price) : '';
          if (count == null) return;
          var noun = count === 1 ? (strings.bag_item_noun_one || 'item') : (strings.bag_item_noun_other || 'items');
          var tpl = strings.bag_updated || 'Bag updated: {{ count }} {{ noun }}, {{ total }}';
          announce(fillTemplate(tpl, { count: count, noun: noun, total: total }).replace(/,\s*$/, ''));
        };
        if (typeof fetch === 'function') {
          fetch('/cart.js', fetchOpts)
            .then(function (r) { return r && r.ok ? r.json() : null; })
            .then(function (cart) { if (cart) go(cart); })
            .catch(function () {});
        }
      }, 250);
    }

    try {
      w.HH.events.on('cart_add', function (props) { announceAdd(props || {}); });
      w.HH.events.on('cart_updated', function (props) { announceUpdated(props || {}); });
      w.HH.events.on('cart_open', function () {
        announce(strings.bag_opened || 'Bag opened');
      });
      w.HH.events.on('cart_close', function () {
        announce(strings.bag_closed || 'Bag closed');
      });
    } catch (_e) {}
  }

  // ---------- Cycle 15 :: opt-in HH events log overlay ----------
  // Renders only when URL has ?hhdebug=1. Listens to document 'hh:event' and
  // shows the last 20 events in a fixed-position panel. No innerHTML.
  try {
    var dbg = new URLSearchParams(location.search).get('hhdebug') === '1';
    if (dbg && !w.HH._eventLogInit) {
      w.HH._eventLogInit = true;

      function buildPanel() {
        var panel = document.createElement('aside');
        panel.id = 'hh-event-log';
        panel.setAttribute('role', 'log');
        panel.setAttribute('aria-live', 'off');
        panel.setAttribute('aria-label', 'HH events log');
        var hdr = document.createElement('header');
        hdr.className = 'hh-event-log__hdr';
        var ttl = document.createElement('strong');
        ttl.textContent = 'HH events';
        var hint = document.createElement('span');
        hint.className = 'hh-event-log__hint';
        hint.textContent = '?hhdebug=1';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hh-event-log__toggle';
        btn.setAttribute('aria-label', 'Toggle event log');
        btn.textContent = '−';
        hdr.appendChild(ttl);
        hdr.appendChild(hint);
        hdr.appendChild(btn);
        var body = document.createElement('ol');
        body.className = 'hh-event-log__body';
        body.reversed = true;
        panel.appendChild(hdr);
        panel.appendChild(body);

        var s = document.createElement('style');
        s.textContent = '#hh-event-log{position:fixed;top:12px;right:12px;z-index:98;width:min(340px,calc(100vw - 24px));max-height:60vh;display:flex;flex-direction:column;font:11px/1.3 ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--c-fg,#1a1814);background:var(--c-bg,#faf6ec);border:1px solid var(--c-border,rgba(0,0,0,.12));border-radius:10px;box-shadow:0 6px 18px rgba(0,0,0,.08);overflow:hidden}#hh-event-log .hh-event-log__hdr{display:flex;align-items:center;gap:8px;padding:6px 10px;border-bottom:1px solid var(--c-border,rgba(0,0,0,.10));background:var(--c-bg,#faf6ec)}#hh-event-log .hh-event-log__hdr strong{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--c-fg,#1a1814)}#hh-event-log .hh-event-log__hint{margin-left:auto;font-size:10px;opacity:.6}#hh-event-log .hh-event-log__toggle{appearance:none;width:22px;height:22px;border-radius:6px;border:1px solid var(--c-border,rgba(0,0,0,.10));background:transparent;color:var(--c-fg,#1a1814);cursor:pointer;font:inherit;line-height:1;display:inline-flex;align-items:center;justify-content:center}#hh-event-log[data-collapsed="true"] .hh-event-log__body{display:none}#hh-event-log[data-collapsed="true"] .hh-event-log__toggle::before{content:"+";display:inline}#hh-event-log[data-collapsed="true"] .hh-event-log__toggle{color:transparent}#hh-event-log[data-collapsed="true"] .hh-event-log__toggle::before{color:var(--c-fg,#1a1814)}#hh-event-log .hh-event-log__body{margin:0;padding:6px 8px;list-style:none;overflow-y:auto;max-height:56vh}#hh-event-log .hh-event-log__row{display:grid;grid-template-columns:auto auto 1fr;gap:6px;padding:3px 0;border-top:1px dashed var(--c-border,rgba(0,0,0,.08));transition:opacity 180ms ease}#hh-event-log .hh-event-log__row:first-child{border-top:0}#hh-event-log .hh-event-log__ts{opacity:.6;font-variant-numeric:tabular-nums}#hh-event-log .hh-event-log__nm{color:var(--c-accent,#b8452c);font-weight:600;white-space:nowrap}#hh-event-log .hh-event-log__props{opacity:.86;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}#hh-event-log[data-fresh="true"] .hh-event-log__row:first-child{opacity:0;animation:hh-event-log-in 180ms ease forwards}@keyframes hh-event-log-in{to{opacity:1}}@media (prefers-reduced-motion: reduce){#hh-event-log .hh-event-log__row{animation:none!important;opacity:1!important;transition:none}}@media (max-width:640px){#hh-event-log{font-size:10px}}';
        document.head.appendChild(s);
        (document.body || document.documentElement).appendChild(panel);

        btn.addEventListener('click', function () {
          panel.dataset.collapsed = panel.dataset.collapsed === 'true' ? 'false' : 'true';
        });

        return { panel: panel, body: body };
      }

      var ui = buildPanel();
      var BUFFER = 20;
      var rows = [];

      function pad2(n) { return n < 10 ? '0' + n : '' + n; }
      function nowTs() {
        var d = new Date();
        return pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds()) + '.' + ('000' + d.getMilliseconds()).slice(-3);
      }
      function propsToString(props) {
        if (!props || typeof props !== 'object') return '';
        try {
          var s = JSON.stringify(props);
          if (s.length > 120) s = s.slice(0, 117) + '...';
          return s;
        } catch (e) {
          return '';
        }
      }

      function appendRow(name, props) {
        var row = document.createElement('li');
        row.className = 'hh-event-log__row';
        var ts = document.createElement('span');
        ts.className = 'hh-event-log__ts';
        ts.textContent = nowTs();
        var nm = document.createElement('span');
        nm.className = 'hh-event-log__nm';
        nm.textContent = String(name || '?');
        var pr = document.createElement('span');
        pr.className = 'hh-event-log__props';
        pr.textContent = propsToString(props);
        row.appendChild(ts);
        row.appendChild(nm);
        row.appendChild(pr);
        var body = ui.body;
        if (body.firstChild) body.insertBefore(row, body.firstChild);
        else body.appendChild(row);
        rows.unshift(row);
        while (rows.length > BUFFER) {
          var dead = rows.pop();
          if (dead && dead.parentNode) dead.parentNode.removeChild(dead);
        }
        ui.panel.dataset.fresh = 'true';
        clearTimeout(window.HH._eventLogFreshTimer);
        window.HH._eventLogFreshTimer = setTimeout(function () { ui.panel.dataset.fresh = 'false'; }, 220);
      }

      document.addEventListener('hh:event', function (e) {
        if (!e || !e.detail) return;
        appendRow(e.detail.name, e.detail.props);
      });
    }
  } catch (_e) {}

})();
